import React, { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ArrowUpDown, Download, Info, Search, SearchX } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import { EmptyState } from "@/components/ui/empty-state";
import { usePermission } from "@/hooks/usePermission";
import { useToast } from "@/hooks/use-toast";
import { cn, formatNumber } from "@/lib/utils";
import { ReportValue, cellText, isNumericColumn, type ReportColumn } from "../lib/columns";
import { downloadWorkbook, rowsToSheet } from "../lib/export";

interface ReportTableProps<Row> {
	columns: ReportColumn<Row>[];
	rows: Row[];
	rowKey: (row: Row, index: number) => string | number;
	/** File name (without extension) and sheet title for the Excel export. */
	exportName: string;
	/** Detail page of a row; its first cell becomes a link and the whole row is clickable. */
	rowHref?: (row: Row) => string | undefined;
	/** Icon of the empty state. */
	emptyIcon: LucideIcon;
	/** Set when the endpoint returned fewer rows than exist (e.g. capped at 1,000). */
	truncated?: { shown: number; total: number };
	/** Initial sort; defaults to the first column that has totals, descending. */
	defaultSort?: { key: string; dir: "asc" | "desc" };
	/** Hide the search box (tiny tables). */
	searchable?: boolean;
	/** Column used as the title of each phone card (default: the first column). */
	cardTitleKey?: string;
	className?: string;
}

/**
 * The table every report ends with: search, sortable headers, a totals row over all matching rows (not just
 * the page), pagination and an Excel export of exactly what the table holds.
 */
export function ReportTable<Row>({
	columns,
	rows,
	rowKey,
	exportName,
	rowHref,
	emptyIcon,
	truncated,
	defaultSort,
	searchable = true,
	cardTitleKey,
	className,
}: ReportTableProps<Row>) {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { toast } = useToast();
	const canExport = usePermission("reports.export");
	const [query, setQuery] = useState("");
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const firstTotal = columns.find(c => c.total);
	const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(
		defaultSort ?? (firstTotal ? { key: firstTotal.key, dir: "desc" } : null),
	);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return rows;
		return rows.filter(row => columns.some(col => cellText(col, row).toLowerCase().includes(q)));
	}, [rows, columns, query]);

	const sorted = useMemo(() => {
		const col = sort && columns.find(c => c.key === sort.key);
		if (!col || !sort) return filtered;
		const factor = sort.dir === "asc" ? 1 : -1;
		return [...filtered].sort((a, b) => {
			const va = col.value(a);
			const vb = col.value(b);
			if (va === vb) return 0;
			if (va === null || va === undefined || va === "") return 1;
			if (vb === null || vb === undefined || vb === "") return -1;
			if (typeof va === "number" && typeof vb === "number") return (va - vb) * factor;
			return String(va).localeCompare(String(vb), i18n.language) * factor;
		});
	}, [filtered, columns, sort, i18n.language]);

	const totals = useMemo(() => {
		const out: Record<string, number> = {};
		for (const col of columns) {
			if (!col.total) continue;
			out[col.key] = sorted.reduce((sum, row) => sum + (Number(col.value(row)) || 0), 0);
		}
		return out;
	}, [columns, sorted]);
	const hasTotals = Object.keys(totals).length > 0;

	// Printing shows every row, not just the current page.
	const [printing, setPrinting] = useState(false);
	useEffect(() => {
		const before = () => flushSync(() => setPrinting(true));
		const after = () => setPrinting(false);
		window.addEventListener("beforeprint", before);
		window.addEventListener("afterprint", after);
		return () => {
			window.removeEventListener("beforeprint", before);
			window.removeEventListener("afterprint", after);
		};
	}, []);

	const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
	const currentPage = Math.min(page, totalPages);
	const pageRows = printing ? sorted : sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

	const toggleSort = (key: string) => {
		setPage(1);
		setSort(prev => (prev?.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));
	};

	const handleExport = () => {
		try {
			const sheet = rowsToSheet(
				columns,
				sorted,
				hasTotals ? { label: t("reports.table.total"), totals } : undefined,
			);
			downloadWorkbook([{ name: exportName, rows: sheet }], exportName, i18n.language);
			toast({ title: t("common.success"), description: t("reports.export.success") });
		} catch {
			toast({ title: t("common.error"), description: t("reports.export.error"), variant: "destructive" });
		}
	};

	const alignClass = (col: ReportColumn<Row>) =>
		col.align === "center"
			? "text-center"
			: col.align === "end" || isNumericColumn(col)
				? "text-end"
				: "text-start";
	const mobileClass = (col: ReportColumn<Row>) => (col.hideOnMobile ? "hidden md:table-cell" : undefined);

	// Phone cards: first column as the title, the main amount (last summed money column) on the side,
	// and the other primary columns as label/value pairs. Secondary (hideOnMobile) columns stay in the export.
	const primaryCol = columns.find(c => c.key === cardTitleKey) ?? columns[0];
	const amountCol = [...columns].reverse().find(c => c.kind === "money" && c.total && c !== primaryCol);
	const detailCols = columns.filter(c => c !== primaryCol && c !== amountCol && !c.hideOnMobile);
	const sortOptions = columns.flatMap(col => [
		{ value: `${col.key}:desc`, label: `${col.label} ↓` },
		{ value: `${col.key}:asc`, label: `${col.label} ↑` },
	]);

	return (
		<div className={cn("overflow-hidden rounded-2xl border bg-card", className)}>
			<div className="flex flex-wrap items-center gap-2 border-b p-3 print:hidden">
				{searchable && (
					<div className="relative w-full sm:w-72">
						<Search
							className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
							aria-hidden="true"
						/>
						<Input
							value={query}
							onChange={e => {
								setQuery(e.target.value);
								setPage(1);
							}}
							placeholder={t("reports.table.search")}
							aria-label={t("reports.table.search")}
							className="h-10 ps-9"
						/>
					</div>
				)}
				<select
					value={sort ? `${sort.key}:${sort.dir}` : ""}
					onChange={e => {
						const [key, dir] = e.target.value.split(":");
						setPage(1);
						setSort(key ? { key, dir: dir === "asc" ? "asc" : "desc" } : null);
					}}
					aria-label={t("reports.table.sortBy")}
					className="h-10 rounded-md border border-input bg-background px-2 text-sm md:hidden"
				>
					{!sort && <option value="">{t("reports.table.sortBy")}</option>}
					{sortOptions.map(o => (
						<option key={o.value} value={o.value}>
							{o.label}
						</option>
					))}
				</select>
				<span className="text-sm text-muted-foreground">
					{t("reports.table.rowCount", { count: sorted.length, formatted: formatNumber(sorted.length) })}
				</span>
				{canExport && (
					<Button
						variant="outline"
						size="sm"
						className="ms-auto"
						onClick={handleExport}
						disabled={sorted.length === 0}
					>
						<Download className="h-4 w-4 me-2" />
						{t("reports.export.excel")}
					</Button>
				)}
			</div>

			{truncated && truncated.total > truncated.shown && (
				<p className="flex items-center gap-2 border-b bg-warning/10 px-4 py-2 text-sm text-warning">
					<Info className="h-4 w-4 shrink-0" aria-hidden="true" />
					{t("reports.table.truncated", {
						shown: formatNumber(truncated.shown),
						total: formatNumber(truncated.total),
					})}
				</p>
			)}

			{rows.length === 0 ? (
				<EmptyState
					icon={emptyIcon}
					title={t("reports.empty.title")}
					description={t("reports.empty.description")}
				/>
			) : sorted.length === 0 ? (
				<EmptyState icon={SearchX} title={t("common.noResults")} />
			) : (
				<>
					<ul className="divide-y md:hidden print:hidden">
						{pageRows.map((row, index) => {
							const href = rowHref?.(row);
							const body = (
								<>
									<div className="flex items-start justify-between gap-3">
										<span className={cn("min-w-0 font-medium", href && "text-primary")}>
											<ReportValue column={primaryCol} row={row} />
										</span>
										{amountCol && (
											<span className="shrink-0 font-semibold">
												<ReportValue column={amountCol} row={row} />
											</span>
										)}
									</div>
									{detailCols.length > 0 && (
										<dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
											{detailCols.map(col => (
												<div key={col.key} className="min-w-0">
													<dt className="text-muted-foreground">{col.label}</dt>
													<dd className="truncate">
														<ReportValue column={col} row={row} />
													</dd>
												</div>
											))}
										</dl>
									)}
								</>
							);
							return (
								<li key={rowKey(row, index)}>
									{href ? (
										<Link
											to={href}
											className="block p-3 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
										>
											{body}
										</Link>
									) : (
										<div className="p-3">{body}</div>
									)}
								</li>
							);
						})}
						{hasTotals && (
							<li className="bg-muted/50 p-3 text-sm">
								<p className="mb-1.5 font-bold">{t("reports.table.total")}</p>
								<dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
									{columns
										.filter(col => col.total)
										.map(col => (
											<div key={col.key}>
												<dt className="text-muted-foreground">{col.label}</dt>
												<dd className="font-semibold">
													<ReportValue column={col} value={totals[col.key]} />
												</dd>
											</div>
										))}
								</dl>
							</li>
						)}
					</ul>
					<div className="hidden md:block print:block">
						<Table className="[&_td]:px-4 [&_th]:px-4">
							<TableHeader>
								<TableRow>
									{columns.map(col => {
										const active = sort?.key === col.key;
										const SortIcon = active
											? sort?.dir === "asc"
												? ArrowUp
												: ArrowDown
											: ArrowUpDown;
										return (
											<TableHead
												key={col.key}
												className={cn(alignClass(col), "whitespace-nowrap", mobileClass(col))}
												aria-sort={
													active
														? sort?.dir === "asc"
															? "ascending"
															: "descending"
														: undefined
												}
											>
												<button
													type="button"
													onClick={() => toggleSort(col.key)}
													className="inline-flex items-center gap-1 rounded hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
												>
													{col.label}
													<SortIcon
														className={cn("h-3 w-3 print:hidden", !active && "opacity-40")}
														aria-hidden="true"
													/>
												</button>
											</TableHead>
										);
									})}
								</TableRow>
							</TableHeader>
							<TableBody>
								{pageRows.map((row, index) => {
									const href = rowHref?.(row);
									return (
										<TableRow
											key={rowKey(row, index)}
											className={cn(href && "cursor-pointer hover:bg-primary/5")}
											onClick={href ? () => navigate(href) : undefined}
										>
											{columns.map((col, colIndex) => (
												<TableCell
													key={col.key}
													className={cn(
														alignClass(col),
														"whitespace-nowrap",
														mobileClass(col),
													)}
												>
													{href && colIndex === 0 ? (
														<Link
															to={href}
															className="font-medium text-primary hover:underline"
															onClick={e => e.stopPropagation()}
														>
															<ReportValue column={col} row={row} />
														</Link>
													) : (
														<ReportValue column={col} row={row} />
													)}
												</TableCell>
											))}
										</TableRow>
									);
								})}
							</TableBody>
							{hasTotals && (
								<TableFooter>
									<TableRow>
										{columns.map((col, colIndex) => (
											<TableCell
												key={col.key}
												className={cn(
													alignClass(col),
													"whitespace-nowrap font-bold",
													mobileClass(col),
												)}
											>
												{col.total ? (
													<ReportValue column={col} value={totals[col.key]} />
												) : colIndex === 0 ? (
													t("reports.table.total")
												) : null}
											</TableCell>
										))}
									</TableRow>
								</TableFooter>
							)}
						</Table>
					</div>
					{sorted.length > 10 && (
						<Pagination
							className="border-t p-3 print:hidden"
							currentPage={currentPage}
							totalPages={totalPages}
							totalItems={sorted.length}
							itemsPerPage={pageSize}
							onPageChange={setPage}
							onPageSizeChange={size => {
								setPageSize(size);
								setPage(1);
							}}
						/>
					)}
				</>
			)}
		</div>
	);
}
